import React, { useState, useEffect } from 'react';
import AdminNav from '../components/AdminNav';
import { Plus, Edit2, Trash2, X, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { forAuthorsService, authService } from '../services/api';

function AdminForAuthors() {
  const navigate = useNavigate();
  const [contentId, setContentId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const parseJson = (value, fallback) => {
    if (!value) return fallback;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  };

  const [importantNotes, setImportantNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [tempNotes, setTempNotes] = useState('');

  const [submissionSteps, setSubmissionSteps] = useState([]);
  const [requirements, setRequirements] = useState([]);

  const [showStepForm, setShowStepForm] = useState(false);
  const [editingStepId, setEditingStepId] = useState(null);
  const [stepForm, setStepForm] = useState({
    stepNumber: '',
    title: '',
    description: '',
    icon: '📌',
  });

  const [showRequirementForm, setShowRequirementForm] = useState(false);
  const [editingRequirementId, setEditingRequirementId] = useState(null);
  const [requirementForm, setRequirementForm] = useState({
    title: '',
    description: '',
    icon: '📌',
  });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await forAuthorsService.get();
        if (response.data && response.data.id) {
          setContentId(response.data.id);
          setImportantNotes(response.data.notes || '');
          setSubmissionSteps(parseJson(response.data.steps, []));
          setRequirements(parseJson(response.data.requirements, []));
        }
      } catch (err) {
        setError('خطا در دریافت محتوای نویسندگان');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, []);

  const saveAll = async (notes = importantNotes, steps = submissionSteps, reqs = requirements) => {
    const data = {
      notes: notes,
      steps: JSON.stringify(steps),
      requirements: JSON.stringify(reqs),
    };
    try {
      if (contentId) {
        await forAuthorsService.update(contentId, data);
      } else {
        const response = await forAuthorsService.create(data);
        setContentId(response.data.id);
      }
      setError('');
    } catch (err) {
      setError('خطا در ذخیره محتوا');
    }
  };

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  // Notes handlers
  const handleSaveNotes = () => {
    setImportantNotes(tempNotes);
    saveAll(tempNotes, submissionSteps, requirements);
    setEditingNotes(false);
  };

  const handleCancelNotes = () => {
    setTempNotes(importantNotes);
    setEditingNotes(false);
  };

  // Step handlers
  const handleAddStep = (e) => {
    e.preventDefault();
    
    let updatedSteps;
    if (editingStepId) {
      updatedSteps = submissionSteps.map(step =>
        step.id === editingStepId
          ? { ...step, ...stepForm }
          : step
      );
      setEditingStepId(null);
    } else {
      const newStep = {
        id: Math.max(...submissionSteps.map(s => s.id), 0) + 1,
        ...stepForm,
      };
      updatedSteps = [...submissionSteps, newStep];
    }

    setSubmissionSteps(updatedSteps);
    saveAll(importantNotes, updatedSteps, requirements);

    setStepForm({
      stepNumber: '',
      title: '',
      description: '',
      icon: '📌',
    });
    setShowStepForm(false);
  };

  const handleEditStep = (step) => {
    setStepForm({
      stepNumber: step.stepNumber,
      title: step.title,
      description: step.description,
      icon: step.icon,
    });
    setEditingStepId(step.id);
    setShowStepForm(true);
  };

  const handleDeleteStep = (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      const updatedSteps = submissionSteps.filter(s => s.id !== id);
      setSubmissionSteps(updatedSteps);
      saveAll(importantNotes, updatedSteps, requirements);
    }
  };

  // Requirement handlers
  const handleAddRequirement = (e) => {
    e.preventDefault();
    
    let updatedRequirements;
    if (editingRequirementId) {
      updatedRequirements = requirements.map(req =>
        req.id === editingRequirementId
          ? { ...req, ...requirementForm }
          : req
      );
      setEditingRequirementId(null);
    } else {
      const newRequirement = {
        id: Math.max(...requirements.map(r => r.id), 0) + 1,
        ...requirementForm,
      };
      updatedRequirements = [...requirements, newRequirement];
    }

    setRequirements(updatedRequirements);
    saveAll(importantNotes, submissionSteps, updatedRequirements);

    setRequirementForm({
      title: '',
      description: '',
      icon: '📌',
    });
    setShowRequirementForm(false);
  };

  const handleEditRequirement = (requirement) => {
    setRequirementForm({
      title: requirement.title,
      description: requirement.description,
      icon: requirement.icon,
    });
    setEditingRequirementId(requirement.id);
    setShowRequirementForm(true);
  };

  const handleDeleteRequirement = (id) => {
    if (window.confirm('آیا مطمئن هستید؟')) {
      const updatedRequirements = requirements.filter(r => r.id !== id);
      setRequirements(updatedRequirements);
      saveAll(importantNotes, submissionSteps, updatedRequirements);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-5xl font-bold mb-2">مدیریت منوی برای نویسندگان</h1>
              <p className="text-xl text-blue-100">ویرایش مراحل ارسال، الزامات و نکات مهم</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg font-semibold transition"
            >
              <LogOut size={20} />
              خروج
            </button>
          </div>
        </div>
      </div>

      <AdminNav />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">در حال بارگذاری...</div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 py-16">
        {error && <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6">{error}</div>}
        {/* Important Notes Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">نکات مهم</h2>

          {editingNotes ? (
            <div className="space-y-4">
              <textarea
                value={tempNotes}
                onChange={(e) => setTempNotes(e.target.value)}
                rows="6"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
              />
              <div className="flex gap-4">
                <button
                  onClick={handleSaveNotes}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition"
                >
                  ذخیره
                </button>
                <button
                  onClick={handleCancelNotes}
                  className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                >
                  انصراف
                </button>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-gray-700 leading-relaxed mb-6 text-lg">{importantNotes}</p>
              <button
                onClick={() => {
                  setEditingNotes(true);
                  setTempNotes(importantNotes);
                }}
                className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
              >
                <Edit2 size={18} />
                ویرایش نکات
              </button>
            </div>
          )}
        </div>

        {/* Submission Steps Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">مراحل ارسال مقاله</h2>
            {!showStepForm && (
              <button
                onClick={() => setShowStepForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition"
              >
                <Plus size={20} />
                افزودن مرحله جدید
              </button>
            )}
          </div>

          {/* Step Form */}
          {showStepForm && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingStepId ? 'ویرایش مرحله' : 'افزودن مرحله جدید'}
                </h3>
                <button
                  onClick={() => {
                    setShowStepForm(false);
                    setEditingStepId(null);
                  }}
                  className="p-2 hover:bg-gray-200 rounded-lg transition"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleAddStep} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">شماره مرحله</label>
                  <input
                    type="number"
                    value={stepForm.stepNumber}
                    onChange={(e) => setStepForm({ ...stepForm, stepNumber: parseInt(e.target.value) })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                  <input
                    type="text"
                    value={stepForm.title}
                    onChange={(e) => setStepForm({ ...stepForm, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="عنوان مرحله"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">توضیح</label>
                  <textarea
                    value={stepForm.description}
                    onChange={(e) => setStepForm({ ...stepForm, description: e.target.value })}
                    required
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="توضیح مرحله"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">آیکون (Emoji)</label>
                  <input
                    type="text"
                    value={stepForm.icon}
                    onChange={(e) => setStepForm({ ...stepForm, icon: e.target.value })}
                    maxLength="2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 text-2xl"
                    placeholder="📝"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
                  >
                    {editingStepId ? 'ذخیره تغییرات' : 'افزودن مرحله'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowStepForm(false);
                      setEditingStepId(null);
                    }}
                    className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Steps Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {submissionSteps.map(step => (
              <div key={step.id} className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200 hover:shadow-lg transition">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="text-4xl">{step.icon}</div>
                    <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">
                      {step.stepNumber}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditStep(step)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteStep(step.id)}
                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Requirements Section */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-3xl font-bold text-gray-900">الزامات مقاله</h2>
            {!showRequirementForm && (
              <button
                onClick={() => setShowRequirementForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-bold hover:from-green-700 hover:to-green-800 transition"
              >
                <Plus size={20} />
                افزودن الزام جدید
              </button>
            )}
          </div>

          {/* Requirement Form */}
          {showRequirementForm && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingRequirementId ? 'ویرایش الزام' : 'افزودن الزام جدید'}
                </h3>
                <button
                  onClick={() => {
                    setShowRequirementForm(false);
                    setEditingRequirementId(null);
                  }}
                  className="p-2 hover:bg-gray-200 rounded-lg transition"
                >
                  <X size={24} className="text-gray-600" />
                </button>
              </div>

              <form onSubmit={handleAddRequirement} className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">عنوان</label>
                  <input
                    type="text"
                    value={requirementForm.title}
                    onChange={(e) => setRequirementForm({ ...requirementForm, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="عنوان الزام"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">توضیح</label>
                  <textarea
                    value={requirementForm.description}
                    onChange={(e) => setRequirementForm({ ...requirementForm, description: e.target.value })}
                    required
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                    placeholder="توضیح الزام"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-900 mb-2">آیکون (Emoji)</label>
                  <input
                    type="text"
                    value={requirementForm.icon}
                    onChange={(e) => setRequirementForm({ ...requirementForm, icon: e.target.value })}
                    maxLength="2"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 text-2xl"
                    placeholder="📄"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition"
                  >
                    {editingRequirementId ? 'ذخیره تغییرات' : 'افزودن الزام'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowRequirementForm(false);
                      setEditingRequirementId(null);
                    }}
                    className="flex-1 px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-bold hover:bg-gray-400 transition"
                  >
                    انصراف
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Requirements Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {requirements.map(requirement => (
              <div key={requirement.id} className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border border-purple-200 hover:shadow-lg transition">
                <div className="flex items-start justify-between mb-4">
                  <div className="text-4xl">{requirement.icon}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditRequirement(requirement)}
                      className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteRequirement(requirement.id)}
                      className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">{requirement.title}</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{requirement.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

export default AdminForAuthors;
